import React from 'react';
import Grid from "@mui/material/Grid";
import {MenuItem, Typography} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";
import CustomText from "../inputs/CustomText";
import {
    codFRegister,
    codFRegisterNone,
    cognomeRegister,
    cognomeRegisterNone,
    emailRegister,
    emailRegisterNone,
    nomeRegister,
    nomeRegisterNone,
    phoneRegister,
    phoneRegisterPrivate,
} from "../../../utils/RegisterUtils";
import {CELLULARE, CODICE_FISCALE, COGNOME, DATA_NASCITA, EMAIL, NOME, SESSO} from "../../../strings/IT";
import {useFormContext} from "react-hook-form";
import CustomDateInput from "../inputs/CustomDateInput";
import {tipiDocumento, tipiDocumentoAnonim} from "../../../utils/arrays/Generics";
import CustomSelect from "../inputs/CustomSelect";
import {referenceAsls, sexReferences} from "../../../utils/dictionaries/Generics";
import {codiciPostaliComuni} from "../../../utils/arrays/CodiciPostaliComuni";
import VirtualizedAutocomplete from "../inputs/VirtualizedAutocomplete";
import AutocompleteSelect from "../inputs/AutocompleteSelect";
import SolidDateInput from "../inputs/SolidDateInput";
import SolidTextField from "../inputs/SolidTextField";

export const AnagraficaCompleta = ({
                                       classes,
                                       intro = "cit",
                                       moreInfo,
                                       name = "",
                                       domReq = false,
                                       resReq = false,
                                       anonimo = false,
                                       fieldSize = "medium",
                                       selectClassName,
                                       solidField = false,
                                       solidSelect = false,
                                   }) => {
    const {errors, watch, control} = useFormContext();
    const TextInput = solidField ? SolidTextField : CustomText;
    const DateInput = solidField ? SolidDateInput : CustomDateInput;
    const documentOptions = (anonimo ? tipiDocumentoAnonim : tipiDocumento).map(value => ({
        value: value.val,
        label: value.label,
    }));
    const sexOptions = Object.entries(sexReferences).map(([key, val]) => ({
        value: key,
        label: val,
    }));
    const birthCityOptionKeys = new Set();
    const birthCityOptions = codiciPostaliComuni
        .map((cod) => {
            const [regione = "", provincia = "", comune = "", codice = ""] = cod.split(",").map((part) => part.trim());
            const label = `${comune}, ${codice}`;

            return {
                value: label,
                label,
                searchText: `${comune} ${codice} ${provincia} ${regione}`,
            };
        })
        .filter((option) => {
            if (birthCityOptionKeys.has(option.value)) {
                return false;
            }
            birthCityOptionKeys.add(option.value);
            return true;
        })
        .sort((first, second) => first.label.localeCompare(second.label));
    const legacyBirthCityOptions = birthCityOptions.map((option) => option.value);

    return (
        <React.Fragment>
            {
                intro === "crp" &&
                <Grid size={12}>
                    <Typography>
                        <b>Anagrafica paziente</b>
                    </Typography>
                    <Typography color={"textSecondary"} style={{display: "flex"}}>
                        I campi obbligatori sono contrassegnati da "*".
                        <Tooltip
                            style={{marginLeft: "auto"}}
                            title={"Inserisci almeno un telefono o una email (o entrambi) per poter registrare la chiamata. In caso il paziente sia sprovvisto di entrambi è necessario chiedere un contatto di una persona a lui vicina per poter essere ricontattato / avere un suo riferimento"}>
                            <InfoTwoToneIcon/>
                        </Tooltip>
                    </Typography>
                </Grid>
            }
            {
                intro === "cit" &&
                <Grid size={12}>
                    <Typography>
                        <b>Compila il form di seguito indicato seguendo le istruzioni:</b>
                    </Typography>
                    <Typography color={"textSecondary"}>
                        Tutti i campi contrassegnati da "*" sono obbligatori. In caso tu non abbia a disposizione un
                        indirizzo email o un telefono ti invito ad indicarne uno di una persona molto vicina a te
                        (moglie,
                        marito, figli, ...). Se non possiedi un codice fiscale usa una delle alternative proposte
                        cliccando sulla
                        freccia.
                    </Typography>
                </Grid>
            }
            <Grid size={{xs: 12, sm: 6}} id={"anagrafica-grid-codf"}>
                <TextInput
                    control={control}
                    rules={watch(name + "tipoDocumento") === "Utente anonimo*" ? codFRegisterNone : (watch(name + "documento") !== "" ? codFRegisterNone : codFRegister)}
                    name={name + "codF"}
                    label={CODICE_FISCALE}
                    autoFocus={false}
                    disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                    inputProps={{
                        className: classes.input
                    }}
                    type={"text"}
                    error={name === "" ? errors["codF"] : errors[name.replace(".", "")]?.["codF"]}
                    size={fieldSize}
                />
            </Grid>
            <Grid size={{xs: 12, sm: 3}}>
                <TextInput
                    control={control}
                    rules={watch(name + "email")?.length > 0 ? phoneRegisterPrivate : phoneRegister}
                    name={name + "cell"}
                    placeholder={"3333333333"}
                    label={CELLULARE + "1*"}
                    autoFocus={false}
                    error={name === "" ? errors["cell"] : errors[name.replace(".", "")]?.["cell"]}
                    autoComplete={"phone"}
                    size={fieldSize}
                />
            </Grid>
            <Grid size={{xs: 12, sm: 3}}>
                <TextInput
                    control={control}
                    rules={phoneRegisterPrivate}
                    name={name + "cell2"}
                    disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                    placeholder={"3333333333"}
                    label={CELLULARE + " 2"}
                    autoFocus={false}
                    error={name === "" ? errors["cell2"] : errors[name.replace(".", "")]?.["cell2"]}
                    size={fieldSize}
                />
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
                {solidSelect ? (
                    <AutocompleteSelect
                        className={selectClassName}
                        control={control}
                        defaultValue={tipiDocumento[0].val}
                        disableClearable
                        error={name === "" ? errors["tipoDocumento"] : errors[name.replace(".", "")]?.["tipoDocumento"]}
                        helperText={(name === "" ? errors["tipoDocumento"] : errors[name.replace(".", "")]?.["tipoDocumento"]) ? "Campo obbligatorio" : ""}
                        label="Metodo identificazione"
                        name={name + "tipoDocumento"}
                        options={documentOptions}
                        rules={{required: true}}
                        size={fieldSize}
                    />
                ) : (
                    <CustomSelect
                        name={name + "tipoDocumento"}
                        label={"Metodo identificazione"}
                        control={control}
                        defaultValue={tipiDocumento[0].val}
                        autoFocus={false}
                        rules={{
                            required: true,
                        }}
                        error={name === "" ? errors["tipoDocumento"] : errors[name.replace(".", "")]?.["tipoDocumento"]}
                    >
                        {
                            (anonimo ? tipiDocumentoAnonim : tipiDocumento).map(value => {
                                return (<MenuItem key={value.val} value={value.val}>{value.label}</MenuItem>)
                            })
                        }
                    </CustomSelect>
                )}
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
                <TextInput
                    control={control}
                    rules={{
                        required:
                            (watch(name + "tipoDocumento") !== "Utente anonimo*" && watch(name + "codF") === "" &&
                                (watch(name + "dataNascita") === "" || watch(name + "dataNascita") === null ||
                                    watch(name + "comuneNascita") === "" || watch(name + "comuneNascita") === null ||
                                    watch(name + "sesso") === "" || watch(name + "sesso") === null))
                    }}
                    name={name + watch("tipoDocumento")}
                    label={"Valore documento"}
                    disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                    autoFocus={false}
                    inputProps={{
                        className: classes.input
                    }}
                    type={"text"}
                    error={name === "" ? errors[(watch("tipoDocumento"))] : errors[name.replace(".", "")]?.[watch("tipoDocumento")]}
                    size={fieldSize}
                />
            </Grid>

            <Grid size={{xs: 12, sm: 6}}>
                <TextInput
                    control={control}
                    rules={watch(name + "tipoDocumento") === "Utente anonimo*" ? cognomeRegisterNone : cognomeRegister}
                    disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                    name={name + "cognome"}
                    label={COGNOME + "*"}
                    placeholder={"Rossi"}
                    autoFocus={false}
                    autoComplete={"family-name"}
                    inputProps={{
                        className: classes.capitalizeInput
                    }}
                    error={name === "" ? errors["cognome"] : errors[name.replace(".", "")]?.["cognome"]}
                    size={fieldSize}
                />
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
                <TextInput
                    control={control}
                    rules={watch(name + "tipoDocumento") === "Utente anonimo*" ? nomeRegisterNone : nomeRegister}
                    disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                    name={name + "nome"}
                    label={NOME + "*"}
                    placeholder={"Giuseppe"}
                    autoFocus={false}
                    autoComplete={"given-name"}
                    inputProps={{
                        className: classes.capitalizeInput
                    }}
                    error={name === "" ? errors["nome"] : errors[name.replace(".", "")]?.["nome"]}
                    size={fieldSize}
                />
            </Grid>

            {/*<Grid item xs={12}>*/}
            {/*    <CustomText*/}
            {/*        control={control}*/}
            {/*        rules={{required: false}}*/}
            {/*        name={name + "infoMedico"}*/}
            {/*        label={"Informazioni MMG"}*/}
            {/*        autoFocus={false}*/}
            {/*        placeholder={"Medico di Medicina Generale"}*/}
            {/*        autoComplete={"infoMedico"}*/}
            {/*        error={name === "" ? errors["infoMedico"] : errors[name.replace(".", "")]?.["infoMedico"]}*/}
            {/*    />*/}
            {/*</Grid>*/}
            {/*<Grid item xs={12} sm={4}>*/}
            {/*    <CustomSelect*/}
            {/*        name={name + "domASL"}*/}
            {/*        label={"Asl domicilio"}*/}
            {/*        control={control}*/}
            {/*        defaultValue={""}*/}
            {/*        disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}*/}
            {/*        autoFocus={false}*/}
            {/*        none={true}*/}
            {/*        noneValue={""}*/}
            {/*        rules={{required: false}}*/}
            {/*        error={name === "" ? errors["domASL"] : errors[name.replace(".", "")]?.["domASL"]}*/}
            {/*    >*/}
            {/*        {Object.entries(referenceAsls).map(([key, val]) => {*/}
            {/*            return (*/}
            {/*                <MenuItem value={key} key={key}>*/}
            {/*                    {val}*/}
            {/*                </MenuItem>*/}
            {/*            );*/}
            {/*        })}*/}
            {/*    </CustomSelect>*/}
            {/*</Grid>*/}
            {/*<Grid item xs={12} sm={4}>*/}
            {/*    <CustomSelect*/}
            {/*        name={name + "resASL"}*/}
            {/*        label={"Asl residenza"}*/}
            {/*        control={control}*/}
            {/*        disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}*/}
            {/*        defaultValue={""}*/}
            {/*        autoFocus={false}*/}
            {/*        none={true}*/}
            {/*        noneValue={""}*/}
            {/*        rules={{required: false}}*/}
            {/*        error={name === "" ? errors["resASL"] : errors[name.replace(".", "")]?.["resASL"]}*/}
            {/*    >*/}
            {/*        {Object.entries(referenceAsls).map(([key, val]) => {*/}
            {/*            return (*/}
            {/*                <MenuItem value={key} key={key}>*/}
            {/*                    {val}*/}
            {/*                </MenuItem>*/}
            {/*            );*/}
            {/*        })}*/}
            {/*    </CustomSelect>*/}
            {/*</Grid>*/}

            {
                moreInfo && <React.Fragment>
                    <Grid size={12}>
                        {solidSelect ? (
                            <AutocompleteSelect
                                className={selectClassName}
                                control={control}
                                disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                                error={name === "" ? errors["comuneNascita"] : errors[name.replace(".", "")]?.["comuneNascita"]}
                                helperText={(name === "" ? errors["comuneNascita"] : errors[name.replace(".", "")]?.["comuneNascita"]) ? "Campo obbligatorio" : ""}
                                label="Comune di nascita"
                                name={name + "comuneNascita"}
                                options={birthCityOptions}
                                rules={{required: (watch(name + "tipoDocumento") !== "Utente anonimo*" && watch(name + "codF") === "" && (watch(name + (watch(name + "tipoDocumento"))) === ""))}}
                                size={fieldSize}
                            />
                        ) : (
                            <VirtualizedAutocomplete
                                name={name + "comuneNascita"}
                                label={"Comune di nascita"}
                                control={control}
                                disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                                options={legacyBirthCityOptions}
                                multiple={false}
                                rules={{required: (watch(name + "tipoDocumento") !== "Utente anonimo*" && watch(name + "codF") === "" && (watch(name + (watch(name + "tipoDocumento"))) === ""))}}
                                error={name === "" ? errors["comuneNascita"] : errors[name.replace(".", "")]?.["comuneNascita"]}
                            />
                        )}
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <DateInput
                            name={name + "dataNascita"}
                            label={DATA_NASCITA}
                            control={control}
                            disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                            defaultValue={null}
                            maxDate={new Date()}
                            rules={{required: (watch(name + "tipoDocumento") !== "Utente anonimo*" && watch(name + "codF") === "" && (watch(name + (watch(name + "tipoDocumento"))) === ""))}}
                            error={name === "" ? errors["dataNascita"] : errors[name.replace(".", "")]?.["dataNascita"]}
                            size={fieldSize}
                        />
                    </Grid>

                    <Grid size={{xs: 12, sm: 6}}>
                        {/*<CustomRadio*/}
                        {/*	name={name + "sesso"}*/}
                        {/*	label={SESSO}*/}
                        {/*	disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}*/}
                        {/*	control={control}*/}
                        {/*	defaultValue={""}*/}
                        {/*	rules={{required: (watch(name + "tipoDocumento") !== "Utente anonimo*" && watch(name + "codF") === "" && (watch(name + (watch(name + "tipoDocumento"))) === ""))}}*/}
                        {/*	error={name === "" ? errors["sesso"] : errors[name.replace(".", "")]?.["sesso"]}*/}
                        {/*>*/}
                        {/*	{*/}
                        {/*		sexValues.map((val, key) => {*/}
                        {/*			return (*/}
                        {/*				<FormControlLabel control={<Radio/>} label={val} value={val} key={key}/>*/}
                        {/*			)*/}
                        {/*		})*/}
                        {/*	}*/}
                        {/*</CustomRadio>*/}
                        {solidSelect ? (
                            <AutocompleteSelect
                                className={selectClassName}
                                control={control}
                                disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                                emptyValue={null}
                                error={name === "" ? errors["sesso"] : errors[name.replace(".", "")]?.["sesso"]}
                                helperText={(name === "" ? errors["sesso"] : errors[name.replace(".", "")]?.["sesso"]) ? "Campo obbligatorio" : ""}
                                label={SESSO}
                                name={name + "sesso"}
                                options={sexOptions}
                                rules={{required: (watch(name + "tipoDocumento") !== "Utente anonimo*" && watch(name + "codF") === "" && (watch(name + (watch(name + "tipoDocumento"))) === ""))}}
                                size={fieldSize}
                            />
                        ) : (
                            <CustomSelect
                                name={name + "sesso"}
                                label={SESSO}
                                control={control}
                                disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                                defaultValue={""}
                                autoFocus={false}
                                none={true}
                                noneValue={null}
                                rules={{required: (watch(name + "tipoDocumento") !== "Utente anonimo*" && watch(name + "codF") === "" && (watch(name + (watch(name + "tipoDocumento"))) === ""))}}
                                error={name === "" ? errors["sesso"] : errors[name.replace(".", "")]?.["sesso"]}
                            >
                                {Object.entries(sexReferences).map(([key, val]) => {
                                    return (
                                        <MenuItem value={key} key={key}>
                                            {val}
                                        </MenuItem>
                                    );
                                })}
                            </CustomSelect>
                        )}
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <TextInput
                            control={control}
                            rules={{required: domReq}}
                            name={name + "indirizzoDomicilio"}
                            label={"Indirizzo di domicilio"}
                            autoFocus={false}
                            error={name === "" ? errors["indirizzoDomicilio"] : errors[name.replace(".", "")]?.["indirizzoDomicilio"]}
                            size={fieldSize}
                        />
                    </Grid>
                    <Grid size={{xs: 12, sm: 6}}>
                        <TextInput
                            control={control}
                            rules={{required: resReq}}
                            name={name + "indirizzoResidenza"}
                            label={"Indirizzo di residenza"}
                            autoFocus={false}
                            error={name === "" ? errors["indirizzoResidenza"] : errors[name.replace(".", "")]?.["indirizzoResidenza"]}
                            size={fieldSize}
                        />
                    </Grid>
                </React.Fragment>
            }
            <Grid size={12}>
                <TextInput
                    control={control}
                    rules={watch(name + "cell")?.length > 0 ? emailRegisterNone : emailRegister}
                    name={name + "email"}
                    label={EMAIL + "*"}
                    disabled={watch(name + "tipoDocumento") === "Utente anonimo*"}
                    autoFocus={false}
                    placeholder={"rossi.giuseppe@gmail.com"}
                    autoComplete={"email"}
                    error={name === "" ? errors["email"] : errors[name.replace(".", "")]?.["email"]}
                    size={fieldSize}
                />
            </Grid>
        </React.Fragment>
    );
}
